<script setup lang="ts">
import { Layer } from "@kaleidoscope-solutions/engine-wasm"
import { DialogSize, KButton, KCheckBox, KDialog } from "@kaleidoscope-solutions/ks-design-system"
import { computed, ref } from "vue"
import { useSetupStore } from "@/stores"
import useAnalytics from "@/utils/analytics"
import { useSnackbar } from "@/utils/useSnackbar"

const { trackEvent } = useAnalytics()
const { addSnackbar } = useSnackbar()
const setupStore = useSetupStore()

function closeDialog() {
  setupStore.exportDialog = false
}

enum Labels {
  User = "Personalization",
  Org = "Configuration and data",
  Base = "Base"
}

const labelToLayer: Record<string, Layer> = {
  [Labels.User]: Layer.User,
  [Labels.Org]: Layer.Org,
  [Labels.Base]: Layer.Base,
}

const checkboxes = ref<Record<Labels, boolean>>({
  [Labels.User]: true,
  [Labels.Org]: true,
  [Labels.Base]: true,
})

const validation = computed(() => !Object.values(checkboxes.value).some((checked) => checked))

function handleExport() {
  try {
    if (!Object.values(checkboxes.value).some((checked) => checked)) {
      addSnackbar({ type: "error", message: `Must select one to export` })
      return
    }

    const selected = []

    for (const [label, checked] of Object.entries(checkboxes.value)) {
      if (!checked) {
        continue
      }

      const opt = labelToLayer[label]
      selected.push(opt)
    }

    setupStore.export(selected)
    trackEvent("export_model")
    addSnackbar({ type: "success", message: `Export successful` })
    closeDialog()
  } catch (error) {
    addSnackbar({ type: "error", message: `Export failed: ${(error as Error).message}` })
  }
}
</script>

<template>
  <KDialog
    open
    :size="DialogSize.small"
    @close="closeDialog"
  >
    <template #default>
      <div
        v-if="setupStore.exportDialog"
        class="size-full flex-1"
      >
        <h2 class="pb-6 font-vastago text-2xl font-bold">
          Export application definition
        </h2>
        <form class="space-y-2">
          <KCheckBox
            v-for="(checked, key) in checkboxes"
            :id="`export-${key}`"
            :key="key"
            v-model="checkboxes[key]"
            :value="key"
          >
            {{ key.charAt(0).toUpperCase() + key.slice(1) }}
          </KCheckBox>
        </form>
        <div class="flex justify-end gap-2 p-2">
          <KButton
            variety="secondary"
            color="violet"
            @click="closeDialog"
          >
            Cancel
          </KButton>
          <KButton
            :disabled="validation"
            @click="handleExport"
          >
            Export
          </KButton>
        </div>
      </div>
    </template>
  </KDialog>
</template>
